import { useState, useEffect, useMemo, useCallback, useReducer, useRef } from "react";
import {
  Grid,
  Chip,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  IconButton,
  Typography,
  TextField,
  Button,
  Switch,
  InputAdornment,
  Box,
  Checkbox,
  FormControlLabel,
  Backdrop,
  CircularProgress,
  Snackbar,
  Modal,
  Fade,
  Tooltip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ExpandMore, Close, InfoOutlined } from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import mixpanel from 'mixpanel-browser';
import moment from 'moment';

// Components
import Categories from "../../components/categories/categories";
import { resolveWeakAreaPrefill } from "./weak-area-prefill";
import UserAgreement from '../../pages/user-agreement/user-agreement';
import Menu from "../../pages/menu/menu";
import ShareEarnModal from "../../components/share-earn-modal/ShareEarnModal";
import QuickstartBanner, {
  buildQuickstartQuizParams,
  shouldShowQuickstart,
} from "./components/quickstart-banner";
import { getExperimentPayload, trackExperimentExposure } from "../../utils/experiments";
import { trackPaywallEntry } from "../../utils/tracking";

// Actions
import {
  userDetails,
  createQuiz,
  getCategories,
  resetQuiz,
  getOverallQuizHistory,
  getOverallStudentQuizHistory,
  resetCreateQuiz,
  getUserActivity,
  getActivePlans,
  setUserActivity,
  getQuesCount,
  resetQuesCount,
  createTutorFeedbackBlock,
  resetCreateTutorFeedbackBlock,
  getCategoryPreferences,
  setCategoryPreferences,
  getPreference,
  setPreference,
  submitQuiz,
  resetSubmitQuiz
} from "../../actions";

import "./create-quiz-page.scss";
import { CONSTANTS } from "../../constants/app-constant";
import PowerSearchPanel, { buildPowerSearchQuizParams } from "./components/power-search-panel";
import { audioModeAvailable } from "../../components/audio-listen-panel/audio-audience";
import { driveModeAvailable } from "../../components/drive-mode-screen/drive-mode-flags";
const CATEGORY_TYPES = {
  DISCIPLINE: "discipline",
  ORGAN_SYSTEM: "organSystem",
  EXAM_TYPE: "examType",
  TOPICS: "topics",
  SUBTOPICS: "subtopics"
};

const ALL_TEXT = {
  DISCIPLINE: "All Discipline",
  ORGAN_SYSTEM: "All Organ System",
  EXAM_TYPE: "All Exam Type"
};

// Exam types that enable the Threshold toggle
const THRESHOLD_ENABLED_EXAM_TYPES = ["ABSITE"];

const DEFAULT_QUESTION_OPTIONS = {
  all: false,
  unused: true,
  correct: false,
  incorrect: false,
  marked: false
};

const DEFAULT_QUESTION_LENGTH = 10;

/*
 * Seed value for the question-count field: the default quiz length, capped to
 * the eligible pool when power-search is on and fewer questions are eligible
 * than the default. Pure + exported so the onboarding-quiz-length treatment
 * semantics are unit-testable. The cap compares against the (experiment-driven)
 * `defaultQuestionLength`, NOT a hardcoded 10 — so a treatment default of 5
 * stays 5 whenever ≥5 questions are eligible, instead of leaking up to the
 * eligible count in [5,10). Byte-identical to the prior `< 10` literal for
 * control (defaultQuestionLength === 10), edge cases included.
 */
export const resolveSeedQuestionLength = (powerSearchOn, eligibleQuestionCount, defaultQuestionLength) =>
  powerSearchOn && eligibleQuestionCount < defaultQuestionLength
    ? eligibleQuestionCount
    : defaultQuestionLength;

// First-paint seed for the question-count field, resolved from the
// onboarding-quiz-length payload using the stored user id (the same canonical
// id the exposure/analysis key on). Resolves the control default (10) while the
// experiment is off/dark or the id is unknown, so it is byte-identical to
// seeding the raw constant — but avoids a 10→5 flash for treatment users on
// first paint. "undefined"/absent id → control, mirroring the page's other
// localStorage userId reads.
const initialSeedQuestionLength = () => {
  const storedId = typeof window !== 'undefined' ? window.localStorage.getItem('userId') : null;
  const unitId = storedId && storedId !== 'undefined' ? storedId : null;
  const { defaultQuestionLength = DEFAULT_QUESTION_LENGTH } =
    getExperimentPayload('onboarding-quiz-length', unitId);
  return defaultQuestionLength;
};

const MIN_QUESTIONS = 1;
const MAX_QUESTIONS = 40;
const DEBOUNCE_DELAY = 300; // 300ms debounce for API calls

// Helper function to debounce function calls
function debounce(func, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

// Join a list of missing requirements into a natural phrase:
// ["an exam type"] -> "an exam type"
// ["a discipline", "an exam type"] -> "a discipline and an exam type"
// ["a", "b", "c"] -> "a, b, and c"
export const joinWithAnd = (items) => {
  if (items.length <= 1) return items.join("");
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

// The backend stamps this name on a tutor-assigned quiz when the tutor left the
// optional name blank (MentorshipManager.assignQuiz) — echoing it inside the
// "Your tutor assigned you a quiz" banner would just repeat the sentence, so
// only a name that differs from it is worth quoting.
const ASSIGNED_QUIZ_DEFAULT_NAME = "Assigned by your tutor";

export const hasCustomAssignedName = (quiz) => {
  const name = typeof quiz?.name === "string" ? quiz.name.trim() : "";
  return name !== "" && name !== ASSIGNED_QUIZ_DEFAULT_NAME;
};

export default function CreateQuizPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [toggleSearchSwitch, setToggleSearchSwitch] = useState(null); // Power search toggle
  const [quizProgressId, setQuizProgressId] = useState();
  // The full row behind quizProgressId, so the resume banner can tell a
  // tutor-assigned quiz (assignedByTutorId, Phase-3 provenance) from a
  // self-created one. Same source array, no extra fetch.
  const [quizProgressQuiz, setQuizProgressQuiz] = useState(null);
  const lastQuesCountPayloadRef = useRef(null);
  const quizSubmittedRef = useRef(false);
  // Whether the user has typed their own question count. While false the
  // seed effect below may (re)fill the field; once true, a quesCount refetch
  // (fired by ANY category/type toggle) must never overwrite the typed value.
  const questionLengthTouchedRef = useRef(false);

  // Category selections
  const [selectedDiscipline, setSelectedDiscipline] = useState([]);
  const [selectedOrgSystem, setSelectedOrgSystem] = useState([]);
  const [selectedExamType, setSelectedExamType] = useState([]);
  const [totalOrgSystem, setTotalOrgSystem] = useState([]);
  const [totalDiscipline, setTotalDiscipline] = useState([]);
  const [examType, setExamType] = useState([]);

  // Power search selections
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedSubTopics, setSelectedSubTopics] = useState([]);
  const [searchResultsData, setSearchResultsData] = useState({ topics: [], subtopics: [] });

  // Question settings
  const [questionOptions, setQuestionOptions] = useState({ ...DEFAULT_QUESTION_OPTIONS });
  // Audio-mode "Has audio" facet (flag+audience gated, default off). Kept OUT of
  // questionOptions state so the question-TYPE selection logic (all/unused/...)
  // never sees it; merged into the payloads at send time. The server scrubs the
  // flag for callers outside its own audience gate — this is presentation-only.
  const [hasAudioFacet, setHasAudioFacet] = useState(false);
  const [questionLength, setQuestionLength] = useState(initialSeedQuestionLength);
  const [selectedQuesTypeLength, setSelectedQuesTypeLength] = useState(true);
  const [max, setMax] = useState(MAX_QUESTIONS);
  const [typeCount, setTypeCount] = useState({
    all: 0,
    unused: 0,
    correct: 0,
    incorrect: 0,
    marked: 0
  });

  // Quiz options
  const [timerMode, setTimerMode] = useState(true);
  const [saveSettings, setSaveSettings] = useState(true);
  const [tutorMode, setTutorMode] = useState(true);
  const [thresholdMode, setThresholdMode] = useState(false);
  // Friendly default (e.g. "Quiz - Jul 3, 4:48 PM") — still unique enough to
  // tell quizzes apart in history, but readable and easy to overtype on mobile
  // (the old `Quiz-7/3/2026, 4:48:12 PM` timestamp was awkward to select/replace).
  const [quizName, setQuizName] = useState(
    `Quiz - ${new Date().toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })}`
  );

  /*
   * Pristine-form gate (F3 create-quiz UX audit 2026-07-06, P1 item 5): a
   * brand-new account used to see five failure signals before doing anything
   * — red "Select at least one option", red zero counts, red "(0 Max)",
   * disabled Create — error styling shouting at a user who hasn't erred.
   * The required-hint/zero-count STYLING stays suppressed until the first
   * builder interaction (any category/type/name/count change, including
   * deletions — emptying a selection is exactly when the hint must appear).
   * Validation and button-disable logic are unchanged; this gates visuals
   * only, identically in both experiment arms.
   */
  const [builderTouched, setBuilderTouched] = useState(false);

  // User & plan info
  const [plan, setPlan] = useState({});
  const [isTutor, setIsTutor] = useState(false);
  const [userRole, setUserRole] = useState('');

  // UI state
  const [isTutorConfirmModal, setIsTutorConfirmModal] = useState(false);
  const [quizErr, setQuizErr] = useState(false);
  const [quizErrMsg, setQuizErrMsg] = useState('');
  const [quizSuccessOpen, setQuizSuccessOpen] = useState(false);
  const [quesCountData, setQuesCountData] = useState({});
  const [isQuestionLimitModalOpen, setIsQuestionLimitModalOpen] = useState(false);
  const [subDateOpen, setSubDateOpen] = useState(false);
  const [openAgreementModal, setOpenAgreementModal] = useState(false);
  const [isCreateQuizDisabled, setIsCreateQuizDisabled] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const classes = useStyles();
  const history = useHistory();
  // Weak-area prefill (readiness-card CTA loop-closer): ephemeral router
  // location state — nothing persisted; validated against the loaded
  // discipline list inside populateCategories (weak-area-prefill.js).
  const weakAreaLocationState = history.location?.state;
  const dispatch = useDispatch();

  // Redux selectors
  const user = useSelector((state) => state.loggedInUser?.user);
  const userDetailsFromToken = useSelector((state) => state.userDetailsFromToken?.userDetails);
  const userId = useSelector((state) => state.studentDetails?.userDetails?.id);
  const userName = useSelector((state) => state.studentDetails?.userDetails?.displayName);
  const categoriesData = useSelector((state) => state.categoriesList);
  const quesCountDataRes = useSelector((state) => state.quesCount?.quesCountDtls);
  const quesCountDataResLoading = useSelector((state) => state.quesCount?.loading);
  const createQuizReq = useSelector((state) => state.createQuiz);
  const overallStudentQuiz = useSelector((state) => state.overallStudentQuiz?.overallStudentQuiz);
  const overallStudentQuizLoading = useSelector((state) => state.overallStudentQuiz?.loading);
  const activePlans = useSelector((state) => state.getActivePlans);
  const createFeedbackBlock = useSelector((state) => state.createTutorFeedbackBlock?.createBlock);
  const userPreferenceData = useSelector((state) => state.getUserPreference?.preference);
  const categoryPreferences = useSelector((state) => state.getCategoryPreferences?.getCategoryPreferences);
  const searchResults = useSelector((state) => state.onTopicsSearch?.topicsSearchResults);
  const submitQuizRes = useSelector((state) => state.submitQuiz?.submitQuiz);
  const overallQuizHistoryState = useSelector((state) => state.overallQuizHistory);

  /*
   * onboarding-quickstart experiment: purely config-driven. The payload's
   * quickstartEnabled decides whether the one-tap first-quiz banner renders;
   * the control payload is false = today's no-banner behavior, so with the
   * experiment flag off (or dark) nothing changes. Reaching the create-quiz
   * page IS the onboarding surface for both arms, so the exposure emit sits
   * here — it no-ops unless the experiment is live, and dedups per unit.
   */
  const { quickstartEnabled } = getExperimentPayload('onboarding-quickstart', userId);
  useEffect(() => {
    if (userId) {
      trackExperimentExposure('onboarding-quickstart', userId);
    }
  }, [userId]);

  /*
   * onboarding-quiz-length experiment: purely config-driven. The payload's
   * defaultQuestionLength seeds the question-count field (the reset effect
   * below). The control payload is 10 = today's DEFAULT_QUESTION_LENGTH, and we
   * fall back to that constant when the key is absent, so with the experiment
   * flag off (or dark, or no userId) the seeded default is byte-identical to
   * today. Reaching the create-quiz page is the shared onboarding surface for
   * both arms, so the exposure emit sits here — it no-ops unless the experiment
   * is live, and dedups per unit.
   */
  const { defaultQuestionLength = DEFAULT_QUESTION_LENGTH } =
    getExperimentPayload('onboarding-quiz-length', userId);
  useEffect(() => {
    if (userId) {
      trackExperimentExposure('onboarding-quiz-length', userId);
    }
  }, [userId]);
  // "Customize instead" hands off to the form for the rest of the tab session,
  // so the banner's Start and the form's Create button are never both the
  // active path. Session- AND user-scoped: a second account logging in on the
  // same tab must not inherit the first account's dismissal. A UX handoff, not
  // an assignment change (the exposure above already fired for this unit).
  // Read at render rather than snapshotted into state: userId resolves async,
  // and a mount-time snapshot would miss the stored dismissal until a remount.
  const quickstartDismissalKey = userId
    ? `quickstartCustomizeDismissed:${userId}`
    : null;
  const quickstartDismissed =
    !!quickstartDismissalKey &&
    window.sessionStorage.getItem(quickstartDismissalKey) === "1";
  // The reducer bump only forces the re-render after the write below.
  const [, bumpQuickstartDismissal] = useReducer((x) => x + 1, 0);
  const onQuickstartCustomize = useCallback(() => {
    if (quickstartDismissalKey) {
      window.sessionStorage.setItem(quickstartDismissalKey, "1");
    }
    bumpQuickstartDismissal();
    document.querySelector(".quiz-header-container")?.scrollIntoView?.({
      behavior: "smooth",
      block: "start",
    });
    // Dismissing unmounts the banner (and the button that held focus), which
    // would drop keyboard/screen-reader focus to <body>. Hand it to the form
    // the student just chose instead — its first control, the quiz name.
    quizNameInputRef.current?.focus();
  }, [quickstartDismissalKey]);
  // Memoized selectors for better performance
  // const userId = useMemo(() => {
  //   return window.localStorage.getItem("userId");
  // }, []);

  useEffect(() => {
    if (searchResults) {
      setSearchResultsData(searchResults);
      // commenting the below two lines to avoid clearing the topics and subtopics, as user shuld be allowed to select the topics and subtopics from different search results
      // setSelectedTopics([]);
      // setSelectedSubTopics([]);
    }
  }, [searchResults]);

  useEffect(() => {
    if (userPreferenceData) {
      if (toggleSearchSwitch !== null) {
        const userId = window.localStorage.getItem("userId");
        dispatch(setPreference({ userId, body: { quizMode: toggleSearchSwitch ? 'POWER_SEARCH' : 'CATEGORY_SEARCH' } }));
      }
    }
  }, [toggleSearchSwitch]);

  // Memoized function to get category map based on current selections
  const getCategoryMap = useCallback(() => {
    const categoryMap = {
      discipline: [...selectedDiscipline],
      organSystem: [...selectedOrgSystem],
      examType: [...selectedExamType]
    };

    return categoryMap;
  }, [selectedDiscipline, selectedOrgSystem, selectedExamType]);

  // Get question count for power search mode (topics/subtopics)
  const getPowerSearchQuestionCount = useCallback(() => {
    if (!userId || !toggleSearchSwitch) return;

    // If no topics or subtopics selected, reset question count to 0 and don't make API call
    if (!selectedTopics.length && !selectedSubTopics.length) {
      setQuesCountData({});
      dispatch(resetQuesCount());
      lastQuesCountPayloadRef.current = null;
      return;
    }

    const { all, ...questionOptionsWithoutAll } = questionOptions;
    const params = {
      userId,
      questionOptions: { ...questionOptionsWithoutAll, hasAudio: hasAudioFacet },
      medicalCategoriesMap: {
        topics: selectedTopics,
        subtopics: selectedSubTopics
      },
      categoryMap: {
        examType: [...selectedExamType]
      },
      quizOptions: {
        thresholdEnabled: thresholdMode
      }
    };

    // Compare with previous payload to avoid duplicate calls
    const payloadString = JSON.stringify(params);
    if (lastQuesCountPayloadRef.current === payloadString) {
      return; // Skip duplicate API call
    }

    // Update the last payload reference
    lastQuesCountPayloadRef.current = payloadString;

    setTimeout(() => {
      if ((toggleSearchSwitch && (selectedExamType.length && (selectedTopics.length || selectedSubTopics.length)))) {
        dispatch(getQuesCount(params));
      } else {
        setQuesCountData({});
        dispatch(resetQuesCount());
      }
    }, 0);

  }, [userId, questionOptions, hasAudioFacet, toggleSearchSwitch, selectedTopics, selectedSubTopics, selectedExamType, thresholdMode, dispatch]);

  // Get question count for category search mode (discipline/organ system/exam type)
  const getCategorySearchQuestionCount = useCallback(() => {
    if (!userId || toggleSearchSwitch) return;

    const { all, ...questionOptionsWithoutAll } = questionOptions;
    const params = {
      userId,
      questionOptions: { ...questionOptionsWithoutAll, hasAudio: hasAudioFacet },
      categoryMap: {
        discipline: [...selectedDiscipline].length ? [...selectedDiscipline] : [...totalDiscipline],
        organSystem: [...selectedOrgSystem],
        examType: [...selectedExamType]
      },
      quizOptions: {
        thresholdEnabled: thresholdMode
      }
    };

    // Compare with previous payload to avoid duplicate calls
    const payloadString = JSON.stringify(params);
    if (lastQuesCountPayloadRef.current === payloadString) {
      return; // Skip duplicate API call
    }

    // Update the last payload reference
    lastQuesCountPayloadRef.current = payloadString;

    setTimeout(() => {
      if (!toggleSearchSwitch && (selectedDiscipline.length && selectedOrgSystem.length && selectedExamType.length)) {
        dispatch(getQuesCount(params));
      } else {
        setQuesCountData({});
        dispatch(resetQuesCount());
      }
    }, 0);

  }, [userId, questionOptions, hasAudioFacet, toggleSearchSwitch, selectedDiscipline, selectedOrgSystem, selectedExamType, thresholdMode, totalDiscipline, dispatch]);

  // Create a debounced version to prevent too many API calls in quick succession
  const debouncedGetQuestionCount = useMemo(
    () => debounce(() => {
      if (toggleSearchSwitch) {
        getPowerSearchQuestionCount();
      } else {
        getCategorySearchQuestionCount();
      }
    }, DEBOUNCE_DELAY),
    [toggleSearchSwitch, getPowerSearchQuestionCount, getCategorySearchQuestionCount]
  );

  // Create a new quiz with current settings
  const onCreateQuiz = useCallback((isConfirm = false) => {
    // Show tutor confirmation modal if needed
    if (isTutor && tutorMode && timerMode && !isConfirm) {
      setIsTutorConfirmModal(true);
      return;
    }
    // Get current category selections for the quiz
    const categoryMap = getCategoryMap();

    // const noOfQuestions = toggleSearchSwitch && quesCountData?.eligibleQuestionCount < 10  ?  quesCountData?.eligibleQuestionCount : questionLength;
    // Prepare quiz creation parameters
    const quizParams = {
      name: quizName.trim(),
      userId,
      quizOptions: {
        tutorOn: isTutor ? tutorMode : false,
        timerOn: timerMode,
        thresholdEnabled: thresholdMode
      },
      noOfQuestions: questionLength,
      questionOptions: { ...questionOptions, hasAudio: hasAudioFacet },
    };

    if (!toggleSearchSwitch) {
      quizParams.categoryMap = categoryMap;
    } else {
      quizParams.medicalCategoriesMap = {
        topics: selectedTopics,
        subtopics: selectedSubTopics
      }
      quizParams.categoryMap = {
        examType: categoryMap.examType
      };
    }

    // Create the quiz
    dispatch(createQuiz(quizParams));

    // Save category preferences if enabled
    if (saveSettings) {
      dispatch(setCategoryPreferences({ userId, categoryMap }));
    }

    // Save user preferences
    const settingsParam = {
      userId,
      body: {
        quizPreference: {
          saveCategoriesPreference: saveSettings,
          timerMode,
          thresholdEnabled: thresholdMode
        }
      }
    };

    if (isTutor) {
      settingsParam.body.quizPreference.tutorMode = tutorMode;
    }

    dispatch(setPreference(settingsParam));

    // Track user activity
    dispatch(setUserActivity({
      userId,
      quizAccessCount: 1,
      questionAttemptCount: questionLength
    }));
  }, [isTutor, tutorMode, timerMode, thresholdMode, getCategoryMap, quizName, userId, questionLength, questionOptions,
    hasAudioFacet, toggleSearchSwitch, dispatch, saveSettings, selectedTopics, selectedSubTopics]);

  // Audio Mode entry (dev-pilot graft of main's onDriveMode, adapted to this
  // page's state — no quizLaunchedRef here because develop lacks main's
  // quiz-config abandonment tracking; the real sync brings both together).
  const onDriveMode = useCallback(() => {
    const categoryMap = getCategoryMap();
    const quizParams = {
      // User-facing default name is "Audio Mode"; the internal route/handler names
      // stay "drive" (deliberate internal/external naming split — "Drive" carries
      // legal driving implications).
      name: quizName.trim() || "Audio Mode",
      userId,
      quizOptions: { tutorOn: false, timerOn: false, thresholdEnabled: thresholdMode },
      noOfQuestions: questionLength,
      questionOptions: { ...questionOptions, hasAudio: true },
    };
    if (toggleSearchSwitch) {
      quizParams.medicalCategoriesMap = { topics: selectedTopics, subtopics: selectedSubTopics };
      quizParams.categoryMap = { examType: categoryMap.examType };
    } else {
      quizParams.categoryMap = categoryMap;
    }
    history.push("/drive", { quizParams });
  }, [getCategoryMap, history, questionLength, questionOptions, quizName, selectedSubTopics,
    selectedTopics, thresholdMode, toggleSearchSwitch, userId]);

  /*
   * One-tap quickstart (onboarding-quickstart treatment, v2). Seeds a first
   * quiz scoped to the exam the student picked in the banner — High-Yield on
   * when that exam supports it (same THRESHOLD_ENABLED_EXAM_TYPES rule as the
   * form's auto-on in handleCategorySelect), every discipline/organ-system,
   * default options — through the SAME createQuiz action as the form; the
   * existing success/error effects below then navigate into the exam or
   * surface the error. Gated by shouldShowQuickstart, whose quickstartEnabled
   * input comes from the experiment payload (control=false), and by the
   * "customize instead" dismissal above.
   */
  const quizNameInputRef = useRef(null);

  const showQuickstart = shouldShowQuickstart({
    quickstartEnabled,
    historyLoading: overallQuizHistoryState?.loading,
    overallQuizHistory: overallQuizHistoryState?.overallQuizHistory,
    inProgressLoading: overallStudentQuizLoading,
    quizProgressId,
    examType,
    discipline: totalDiscipline,
    organSystem: totalOrgSystem,
  });

  const quickstartHighYieldExamTypes =
    CONSTANTS.thresholdFeatureEnabled ? THRESHOLD_ENABLED_EXAM_TYPES : [];

  const onQuickstart = useCallback((chosenExamType) => {
    if (createQuizReq?.loading) {
      return; // double-tap guard while the seeded quiz is being created
    }
    if (!chosenExamType) {
      return; // exam-picker guardrail: never seed without an explicit exam
    }
    dispatch(createQuiz(buildQuickstartQuizParams({
      userId,
      exam: chosenExamType,
      highYield: CONSTANTS.thresholdFeatureEnabled && THRESHOLD_ENABLED_EXAM_TYPES.includes(chosenExamType),
      discipline: totalDiscipline,
      organSystem: totalOrgSystem,
      isTutor,
      tutorMode,
      timerMode,
    })));
  }, [createQuizReq?.loading, dispatch, userId, totalDiscipline, totalOrgSystem, isTutor, tutorMode, timerMode]);

  /*
   * "Practice these questions" from the Power Search panel. Funnels the
   * semantic result ids through the SAME createQuiz action as the form
   * (backend re-validates the ids; flag-gated dark end to end); the existing
   * success/error effects below then navigate into the exam or surface the
   * error, exactly like a form-created quiz. ids/query/examTypes are the
   * panel's snapshots of the SERVED search — deliberately NOT the click-time
   * form selection, so re-picking an exam between search and click cannot
   * drift the quiz's scope away from the results it contains.
   */
  const onPowerSearchBuildQuiz = useCallback((questionIds, query, examTypes) => {
    if (createQuizReq?.loading) {
      return; // double-tap guard shared with the form/quickstart paths
    }
    if (!questionIds?.length) {
      return;
    }
    dispatch(createQuiz(buildPowerSearchQuizParams({
      userId,
      questionIds,
      query,
      examTypes,
      isTutor,
      tutorMode,
      timerMode,
    })));
  }, [createQuizReq?.loading, dispatch, userId, isTutor, tutorMode, timerMode]);

  useEffect(() => {
    // defaultQuestionLength comes from the onboarding-quiz-length experiment
    // payload; it resolves to DEFAULT_QUESTION_LENGTH (10) whenever the
    // experiment is off/dark, so this is byte-identical to today by default.
    // The eligible-pool cap compares against that default (not a literal 10),
    // so a treatment default of 5 is not overridden up to the eligible count.
    //
    // SEED, not sync: this effect refires on every quesCount refetch — which
    // ANY category/question-type toggle triggers — and used to overwrite a
    // count the user had already typed (type 20, tick a discipline, field
    // silently reads 10 again). It now fills the field only while the user
    // hasn't touched it; after that, a stale-high typed value fails closed
    // through the existing eligible-count validation instead of being
    // silently rewritten.
    if (questionLengthTouchedRef.current) {
      return;
    }
    const noOfQuestions = resolveSeedQuestionLength(toggleSearchSwitch, quesCountData?.eligibleQuestionCount, defaultQuestionLength);
    setQuestionLength(noOfQuestions);
  }, [quesCountData, defaultQuestionLength]);

  // Update create quiz button disabled state based on conditions
  useEffect(() => {
    // const shouldDisable = quesCountDataResLoading !== false || (!questionLength || questionLength > quesCountData?.eligibleQuestionCount);
    // setIsCreateQuizDisabled(shouldDisable);

    const disableCreateQuizBtn = !questionLength ||
    questionLength > quesCountData?.eligibleQuestionCount ||
    (!toggleSearchSwitch && (!selectedDiscipline.length || !selectedOrgSystem.length || !selectedExamType.length)) ||
    (toggleSearchSwitch && (!selectedExamType.length || (!selectedTopics.length && !selectedSubTopics.length))) ||
    !quizName.trim()

    setIsCreateQuizDisabled(disableCreateQuizBtn);
  }, [questionLength, quesCountData?.eligibleQuestionCount, quesCountDataResLoading, toggleSearchSwitch,selectedDiscipline, selectedOrgSystem, selectedExamType, selectedTopics, selectedSubTopics, quizName ]);

  // Generic handler for clearing or selecting all items in a category
  const clearSelection = useCallback((event, type) => {
    setBuilderTouched(true);
    if (event) {
      event.stopPropagation();
    }

    switch (type) {
      case CATEGORY_TYPES.DISCIPLINE:
        setSelectedDiscipline(selectedDiscipline.length ? [] : [...totalDiscipline]);
        break;
      case CATEGORY_TYPES.ORGAN_SYSTEM:
        setSelectedOrgSystem(selectedOrgSystem.length ? [] : [...totalOrgSystem]);
        break;
      case CATEGORY_TYPES.EXAM_TYPE:
        setSelectedExamType(selectedExamType.length ? [] : [...examType]);
        break;
      case CATEGORY_TYPES.TOPICS:
        setSelectedTopics(selectedTopics.length ? [] :
          (searchResultsData?.topics?.length ? [...searchResultsData.topics] : []));
        break;
      case CATEGORY_TYPES.SUBTOPICS:
        setSelectedSubTopics(selectedSubTopics.length ? [] :
          (searchResultsData?.subtopics?.length ? [...searchResultsData.subtopics] : []));
        break;
      default:
        break;
    }
  }, [
    selectedDiscipline, totalDiscipline,
    selectedOrgSystem, totalOrgSystem,
    selectedExamType, examType,
    selectedTopics, selectedSubTopics,
    searchResultsData
  ]);

  // Delete all categories of a specific type
  const handleDeleteAllCategory = useCallback((type) => {
    setBuilderTouched(true);
    switch (type) {
      case CATEGORY_TYPES.DISCIPLINE:
        setSelectedDiscipline([]);
        break;
      case CATEGORY_TYPES.ORGAN_SYSTEM:
        setSelectedOrgSystem([]);
        break;
      case CATEGORY_TYPES.EXAM_TYPE:
        setSelectedExamType([]);
        break;
      case CATEGORY_TYPES.TOPICS:
        setSelectedTopics([]);
        break;
      case CATEGORY_TYPES.SUBTOPICS:
        setSelectedSubTopics([]);
        break;
      default:
        break;
    }
  }, []);

  // Delete a single category item
  const handleDeleteCategory = useCallback((type, item) => {
    setBuilderTouched(true);
    switch (type) {
      case CATEGORY_TYPES.DISCIPLINE:
        setSelectedDiscipline(prev => prev.filter(i => i !== item));
        break;
      case CATEGORY_TYPES.ORGAN_SYSTEM:
        setSelectedOrgSystem(prev => prev.filter(i => i !== item));
        break;
      case CATEGORY_TYPES.EXAM_TYPE:
        setSelectedExamType(prev => prev.filter(i => i !== item));
        break;
      case CATEGORY_TYPES.TOPICS:
        setSelectedTopics(prev => prev.filter(i => i !== item));
        break;
      case CATEGORY_TYPES.SUBTOPICS:
        setSelectedSubTopics(prev => prev.filter(i => i !== item));
        break;
      default:
        break;
    }
  }, []);

  // Handle category selection updates
  const handleCategorySelect = useCallback((type, items) => {
    setBuilderTouched(true);
    switch (type) {
      case CATEGORY_TYPES.DISCIPLINE:
        setSelectedDiscipline([...items]);
        break;
      case CATEGORY_TYPES.ORGAN_SYSTEM:
        setSelectedOrgSystem([...items]);
        break;
      case CATEGORY_TYPES.EXAM_TYPE:
        setSelectedExamType([...items]);
        if (CONSTANTS.thresholdFeatureEnabled && items.some(exam => THRESHOLD_ENABLED_EXAM_TYPES.includes(exam))) {
          setThresholdMode(true);
        }
        break;
      case CATEGORY_TYPES.TOPICS:
        setSelectedTopics([...items]);
        break;
      case CATEGORY_TYPES.SUBTOPICS:
        setSelectedSubTopics([...items]);
        break;
      default:
        break;
    }
  }, []);

  // Delegating to the common handler
  const onDisciplineCatSelect = useCallback((items) => {
    handleCategorySelect(CATEGORY_TYPES.DISCIPLINE, items);
  }, [handleCategorySelect]);

  const onOrgSysCatSelect = useCallback((items) => {
    handleCategorySelect(CATEGORY_TYPES.ORGAN_SYSTEM, items);
  }, [handleCategorySelect]);

  const onExamTypeCatSelect = useCallback((items) => {
    handleCategorySelect(CATEGORY_TYPES.EXAM_TYPE, items);
  }, [handleCategorySelect]);

  // A3: pickable exam types inside the RIGHT accordion. Toggling one item routes
  // through the same handleCategorySelect the left rail uses, so selection stays
  // single-sourced (and keeps the ABSITE High-Yield auto-on rule). A first-timer
  // who wants exactly one exam can now pick it where the blocking hint appears,
  // instead of being forced to the narrow left rail or the over-scoping "Add All".
  const toggleExamType = useCallback((item, checked) => {
    const next = checked
      ? [...selectedExamType, item]
      : selectedExamType.filter((exam) => exam !== item);
    handleCategorySelect(CATEGORY_TYPES.EXAM_TYPE, next);
  }, [selectedExamType, handleCategorySelect]);

  /*
   * Quickstart exam pick → form sync. The banner's chip pick and the form's
   * Exam Type selector were two contradicting sources of truth on one screen:
   * a user who picked ABSITE in the banner still saw the form's red "Select
   * at least one option" beside a disabled Create Quiz (F3 create-quiz UX
   * audit 2026-07-06, P1 item 2). Writing the pick through the SAME handler
   * the form uses keeps one source of truth — including its ABSITE
   * High-Yield auto-on rule. Treatment-only by construction: the banner is
   * the only caller, and it renders only on the treatment payload.
   */
  const onQuickstartExamPick = useCallback((exam) => {
    if (exam) {
      handleCategorySelect(CATEGORY_TYPES.EXAM_TYPE, [exam]);
    }
  }, [handleCategorySelect]);

  const onTopicsCatSelect = useCallback((items) => {
    handleCategorySelect(CATEGORY_TYPES.TOPICS, items);
  }, [handleCategorySelect]);

  const onSubTopicsCatSelect = useCallback((items) => {
    handleCategorySelect(CATEGORY_TYPES.SUBTOPICS, items);
  }, [handleCategorySelect]);

  useEffect(() => {
    if (userPreferenceData) {
      const role = window.localStorage.getItem("role");

      if (!userPreferenceData?.userAcknowledged) {
        if (role !== 'ROLE_ADMIN') {
          setOpenAgreementModal(true);
        }
      }
      if (userPreferenceData?.quizPreference) {
        setTimerMode(userPreferenceData.quizPreference.timerMode);
        setSaveSettings(userPreferenceData.quizPreference.saveCategoriesPreference);
        setTutorMode(userPreferenceData.quizPreference.tutorMode);
        setThresholdMode(userPreferenceData.quizPreference.thresholdEnabled || false);
        populateCategories(userPreferenceData.quizPreference.saveCategoriesPreference)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPreferenceData]);

  useEffect(() => {
    if (userDetailsFromToken) {
      window.localStorage.setItem("userId", userDetailsFromToken.id);
      window.localStorage.setItem("role", userDetailsFromToken.primaryRole);

      if (userDetailsFromToken.primaryRole !== 'ROLE_ADMIN' && userPreferenceData && !userPreferenceData?.userAcknowledged) {
        setOpenAgreementModal(true);
        return;
      }

      setRole(userDetailsFromToken.primaryRole);

      const userData = {
        name: userDetailsFromToken?.displayName,
        email: userDetailsFromToken?.email
      };
      window.localStorage.setItem("userdetails", JSON.stringify(userData));

      if (!userName) {
        dispatch(userDetails(userDetailsFromToken.id));
      }
      dispatch(getUserActivity(userDetailsFromToken.id));
      dispatch(getActivePlans(userDetailsFromToken.id));
      dispatch(getCategoryPreferences(userDetailsFromToken.id));

      if (userDetailsFromToken && !userName) {
        mixpanel.track('Sign In', {
          $name: userDetailsFromToken?.displayName,
          $user_id: userDetailsFromToken?.displayName,
          email: userDetailsFromToken?.email,
          role: userDetailsFromToken?.primaryRole
        });
        mixpanel.identify(userDetailsFromToken?.displayName);
        mixpanel.people.set({
          $email: userDetailsFromToken?.displayName,
          $name: userDetailsFromToken?.email,
          $role: userDetailsFromToken?.primaryRole
        });
        mixpanel.track("Menu-create-quiz", {
          $name: userDetailsFromToken?.displayName,
          email: userDetailsFromToken?.email,
          role: userDetailsFromToken?.primaryRole
        });
      }
    }
  }, [dispatch, userDetailsFromToken, userName, userPreferenceData]);

  useEffect(() => {
    if (createFeedbackBlock?.blockId) {
      window.localStorage.setItem("feedbackBlockId", createFeedbackBlock?.blockId);
      dispatch(resetCreateTutorFeedbackBlock());
      history.push(`exam/${createFeedbackBlock?.quizId}/1/tutor/1`);
    }
  }, [createFeedbackBlock, createQuizReq, dispatch, history]);

  useEffect(() => {
    if (quesCountDataRes) {
      setQuesCountData(quesCountDataRes);
    }
  }, [quesCountDataRes]);

  useEffect(() => {
    const userinfos = JSON.parse(localStorage.getItem('userdetails'));
    const userId = window.localStorage.getItem("userId");
    const userrole = window.localStorage.getItem("role");

    if (createQuizReq?.createQuizRes && createQuizReq?.createQuizRes?.id) {
      if (isTutor && tutorMode) {
        dispatch(createTutorFeedbackBlock({
          quizId: createQuizReq.createQuizRes.id,
          userId
        }));
      } else {
        history.push(`exam/${createQuizReq.createQuizRes.id}/1`);
      }

      dispatch(resetCreateQuiz());
      mixpanel.track("Create Quiz", {
        Questioncount: questionLength,
        $name: userinfos?.name,
        email: userinfos?.email,
        role: userrole
      });
    } else if (Object.keys(createQuizReq).length && !createQuizReq?.loading && !createQuizReq.createQuizReq) {
      setQuizErr(true);
      // Kept as a backstop: the disabled button now blocks the normal empty-name
      // click, but the tutor-mode confirm path (onCreateQuiz(true) after the
      // modal) can still submit — defense-in-depth, not dead code.
      if (!quizName.trim()) {
        setQuizErrMsg('Quiz Name is required');
      } else {
        setQuizErrMsg(createQuizReq?.errMsg);
      }

      dispatch(resetCreateQuiz());
    }
  }, [createQuizReq, dispatch, history, isTutor, questionLength, tutorMode]);

  useEffect(() => {
    let userId;

    if (!user) {
      userId = window.localStorage.getItem("userId");
      const role = window.localStorage.getItem("role");
      setRole(role);

    } else if (user && user.id) {
      window.localStorage.setItem("userId", user.id);
      window.localStorage.setItem("role", user.primaryRole);
      setRole(user.primaryRole);
      userId = user.id;
    }

    if (userId && userId !== "undefined") {
      if (!userName) {
        dispatch(userDetails(userId));
        dispatch(getUserActivity(userId));
      }
      dispatch(getOverallQuizHistory(userId));

      dispatch(getOverallStudentQuizHistory({ userId: userId, isCompleted: false }));

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user]);

  const setRole = (role) => {
    if (role === 'ROLE_TUTOR') {
      setUserRole('Tutor');
    } else if (role === 'ROLE_ADMIN') {
      setUserRole('Admin');
    }
  }

  useEffect(() => {
    // Array-only: fetch errors arrive through the SUCCESS action as a
    // {status, errMsg} object (doGetCall resolves instead of rejecting and the
    // saga only branches on errStatus), so a truthy check would resolve
    // quizProgressId to "" on error — falsely asserting "no quiz in progress"
    // to the quickstart gate.
    if (Array.isArray(overallStudentQuiz)) {
      let lastProgressId = "";
      let lastProgressQuiz = null;

      for (let i = overallStudentQuiz.length - 1; i >= 0; i--) {
        if (overallStudentQuiz[i].quizStatus === "IN_PROGRESS") {
          lastProgressId = overallStudentQuiz[i].id;
          lastProgressQuiz = overallStudentQuiz[i];
          break;
        }
      }

      setQuizProgressId(lastProgressId);
      setQuizProgressQuiz(lastProgressQuiz);
    }
  }, [overallStudentQuiz]);

  useEffect(() => {
    if (categoriesData.loading === undefined) {
      dispatch(getCategories());
    }

    const userId = window.localStorage.getItem("userId");

    if (userId) {
      dispatch(getPreference(userId));
      dispatch(getCategoryPreferences(userId));
    }

    return () => {
      setQuesCountData({});
      dispatch(resetQuesCount());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (categoriesData && categoriesData.loading !== undefined) {
      setIsLoading(categoriesData.loading);
      if (categoriesData?.categoriesList && categoryPreferences !== undefined) {
        populateCategories();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriesData, categoryPreferences, userPreferenceData?.quizPreferences]);

  useEffect(() => {
    if (quesCountData) {
      let questionLmt = quesCountData?.eligibleQuestionCount;

      setMax(questionLmt);

      const role = window.localStorage.getItem("role");
      if (role === 'ROLE_ADMIN' || role === 'ROLE_TUTOR') {
        setMax(MAX_QUESTIONS);
        questionLmt = DEFAULT_QUESTION_LENGTH;
      }

      if (!openAgreementModal && quesCountData?.questionLeftInDay <= 0 && !(role === 'ROLE_ADMIN' || role === 'ROLE_TUTOR')) {
        setIsQuestionLimitModalOpen(true);
      } else {
        setIsQuestionLimitModalOpen(false);
      }

      setRole(role);

      if (role === 'ROLE_TUTOR') {
        setIsTutor(true);
        setTimerMode(false);
      }

      const hasNotified = window.localStorage.getItem('hasNotified');

      if (!hasNotified && questionLmt && !openAgreementModal) {
        window.localStorage.setItem('hasNotified', true);
        // setSubDateOpen(true); // commenting this for now to avoid the subscription modal from popping up
      }
    }
  }, [openAgreementModal, quesCountData]);

  useEffect(() => {
    if (userId) {
      debouncedGetQuestionCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleSearchSwitch, userId]);

  useEffect(() => {
    const savedUserId = window.localStorage.getItem("userId");

    if (savedUserId && activePlans?.loading === undefined) {
      dispatch(getActivePlans(savedUserId));
    } else if (activePlans?.plansList) {
      let activePkg;

      if (activePlans?.plansList?.length) {
        activePkg = activePlans?.plansList?.filter(item => item.iselected)[0];
      }

      setPlan(activePkg || {});
    }
  }, [activePlans, dispatch]);

  const populateCategories = (newSavedSettings = null) => {
    if (categoriesData?.categoriesList?.discipline?.length) {
      const totalDisciplineList = categoriesData?.categoriesList?.discipline;
      const totalOrgSystemList = categoriesData?.categoriesList?.organSystem;
      const allExamTypesList = categoriesData?.categoriesList?.examType;
      // Readiness-card prefill wins over prefs-vs-all for DISCIPLINE only;
      // unknown/garbage names resolve to [] and the normal behavior below
      // takes over untouched. Organ systems / exam types are not prefilled.
      const prefillDisciplines = resolveWeakAreaPrefill(
        weakAreaLocationState,
        totalDisciplineList
      );

      if (categoryPreferences !== undefined) {
        const userSelectedDisciplines = categoryPreferences?.discipline || [];
        const userSelectedOrgSystems = categoryPreferences?.organSystem || [];
        const userSelectedExamTypes = categoryPreferences?.examType || [];

        const selectedDisciplineList = prefillDisciplines.length
          ? prefillDisciplines
          : (newSavedSettings !== null ? newSavedSettings : saveSettings) &&
            userSelectedDisciplines.length ? userSelectedDisciplines : totalDisciplineList;

        const selectedOrgSystemList = (newSavedSettings !== null ? newSavedSettings : saveSettings) &&
          userSelectedOrgSystems.length ? userSelectedOrgSystems : totalOrgSystemList;

        const selectedExamTypeList = userSelectedExamTypes || [];

        setSelectedDiscipline([...selectedDisciplineList]);
        setTotalDiscipline([...totalDisciplineList]);

        setSelectedOrgSystem([...selectedOrgSystemList]);
        setTotalOrgSystem([...totalOrgSystemList]);

        setSelectedExamType([...selectedExamTypeList]);
        setExamType([...allExamTypesList]);
      } else {
        setTotalDiscipline([...totalDisciplineList]);
        setTotalOrgSystem([...totalOrgSystemList]);
        setExamType([...allExamTypesList]);

        setSelectedDiscipline(prefillDisciplines.length ? [...prefillDisciplines] : []);
        setSelectedOrgSystem([]);
        setSelectedExamType([]);
      }
    }
  };

  // Option toggle handlers
  const toggleTimerMode = useCallback((evt) => {
    setTimerMode(evt.target.checked);
  }, []);

  const toggleSaveSettings = useCallback((evt) => {
    setSaveSettings(evt.target.checked);
  }, []);

  const toggleTutorMode = useCallback((evt) => {
    setTutorMode(evt.target.checked);
  }, []);

  const toggleThresholdMode = useCallback((evt) => {
    setThresholdMode(evt.target.checked);
  }, []);

  // Form input handlers
  const changeQuizName = useCallback((evt) => {
    setBuilderTouched(true);
    setQuizName(evt.target.value);
  }, []);

  const changeQuestionsLength = useCallback((event) => {
    setBuilderTouched(true);

    // Strictly allow only digits 0-9, block everything else
    const input = event.target.value.trim();

    let value = input;
    value = value ? parseInt(value) : "";
    if (!/^\d*$/.test(input) || (value !== "" && (value > max || value < MIN_QUESTIONS))) {
      return;
    }

    // The field is now user-owned: the seed effect stops refilling it on
    // quesCount refetches (see the touched guard above).
    questionLengthTouchedRef.current = true;
    setQuestionLength(value);
  }, [max]);

  // Question type selection handler
  const selectQuestionType = useCallback((evt, type, countObj) => {
    setBuilderTouched(true);
    if (evt && type) {
      // Update the question options based on the checkbox change
      setQuestionOptions(prev => {
        const updated = { ...prev, [type]: evt.target.checked };

        // If "all" is selected, automatically set unused, correct, and incorrect as true
        if (type === "all" && evt.target.checked) {
          updated.unused = true;
          updated.correct = true;
          updated.incorrect = true;
          // Marked can remain as is since it depends on correct/incorrect being selected
        }

        // If "all" is unchecked, uncheck unused, correct, and incorrect
        if (type === "all" && !evt.target.checked) {
          updated.unused = false;
          updated.correct = false;
          updated.incorrect = false;
          updated.marked = false; // Also uncheck marked since it depends on correct/incorrect
        }

        // If any of unused, correct, or incorrect is unchecked, uncheck "all"
        if ((type === "unused" || type === "correct" || type === "incorrect") && !evt.target.checked) {
          updated.all = false;
        }

        // If all three (unused, correct, incorrect) are selected, automatically select "all"
        if (updated.unused && updated.correct && updated.incorrect) {
          updated.all = true;
        }

        // If both correct & incorrect are unchecked, ensure marked is also unchecked
        if (!updated.incorrect && !updated.correct) {
          updated.marked = false;
        }

        // Determine if at least one question-type is selected after the change
        const isAnySelected = Object.values(updated).some(val => val);
        setSelectedQuesTypeLength(isAnySelected);

        // If none selected, clear question-count data so the UI reflects 0
        if (!isAnySelected) {
          setQuesCountData({});
          setQuestionLength(0);
          // This zero is deliberate destruction of whatever was in the field
          // (typed or seeded) — hand ownership back to the seed effect, so
          // re-selecting a question type refills the default instead of
          // stranding the field at 0 behind the touched guard.
          questionLengthTouchedRef.current = false;
          // Also reset the payload reference so that when user re-selects a
          // question type, the payload is considered "new" and the API call
          // fires again. Otherwise the dedup logic would incorrectly skip it
          // if the params match a previous request.
          lastQuesCountPayloadRef.current = null;
        }

        return updated;
      });
    } else if (countObj) {
      // Handle updating the per-type counts coming from the API response
      setTypeCount({
        all: countObj.all || 0,
        unused: countObj.unused || 0,
        correct: countObj.correct || 0,
        incorrect: countObj.incorrect || 0,
        marked: countObj.marked || 0
      });
    }
  }, []);

  const onResumeQuiz = () => {
    if (isTutor && tutorMode) {
      history.push(`exam/${quizProgressId}/1/tutor/1`);
    } else {
      history.push(`exam/${quizProgressId}/1`);
    }
  };

  const onEndQuiz = () => {

    const requestPayload = {
      quizEndMode: "ADHOC",
      quizHistory: {
        quizSubmitDate: moment().format('YYYY-MM-DDTHH:mm:ss.sss') + 'Z',
        quizId: quizProgressId,
        userId: localStorage.getItem("userId")
      },
    };

    quizSubmittedRef.current = true;
    dispatch(submitQuiz(requestPayload));
  };

  const examTypeOPtionsSelection = () => {
    // Copy before sorting — Array.prototype.sort mutates in place, and this is
    // the exam-type list from state.
    const sortedExamTypes = [...examType].sort((a, b) => a.localeCompare(b));
    return (
      <>
        <Accordion defaultExpanded className="quiz-selections">
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls="panel-exam-type-content"
            id="panel-exam-type-header"
          >
            <Typography className={`header ${classes.heading}`}>Exam Type</Typography>
            <Button
              disableElevation
              className={classes.clearSelection}
              color="primary"
              onClick={(evt) => clearSelection(evt, "examType")}
            >
              {selectedExamType.length > 0 && <>Clear All</>}
              {!selectedExamType.length && <>Add All</>}
            </Button>
          </AccordionSummary>
          <AccordionDetails id="panel-exam-type-content" className="accordian-summary exam-type-picker">
            {/* A3: pickable exam-type options right where the blocking hint
                appears, so a first-timer wanting ONE exam isn't forced to the
                narrow left rail or the over-scoping "Add All". */}
            <FormGroup className="exam-type-options">
              {sortedExamTypes.map((item) => (
                <FormControlLabel
                  key={item}
                  className="exam-type-option"
                  control={
                    <Checkbox
                      color="primary"
                      checked={selectedExamType.includes(item)}
                      onChange={(evt) => toggleExamType(item, evt.target.checked)}
                      name={item}
                      inputProps={{ "data-testid": `right-examtype-${item}` }}
                    />
                  }
                  label={item}
                />
              ))}
            </FormGroup>

            {builderTouched && !selectedExamType.length && (
              <Typography className="no-select">
                Select at least one option
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      </>
    )
  }
  const renderQuizOptionSelections = () => {
    return (
      <>
        {!toggleSearchSwitch ? (
          <>
            <Accordion defaultExpanded className="quiz-selections">
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="panel-discipline-content"
                id="panel-discipline-header"
              >
                <Typography className={`header ${classes.heading}`}>Discipline</Typography>
                <Button
                  disableElevation
                  className={classes.clearSelection}
                  color="primary"
                  onClick={(evt) => clearSelection(evt, "discipline")}
                >
                  <>Clear All</>
                  {/* commenting this out as for discipline we are showing all the options even when no option is selected */}
                  {/* {selectedDiscipline.length > 0 && <>Clear All</>} */}
                  {/* {!selectedDiscipline.length && <>Add All</>} */}
                </Button>
              </AccordionSummary>
              <AccordionDetails id="panel-discipline-content" className="accordian-summary">
                {(selectedDiscipline.length === totalDiscipline.length || selectedDiscipline.length === 0) && (
                  <Chip
                    className="category-chip"
                    label={ALL_TEXT.DISCIPLINE}
                    onDelete={() => handleDeleteAllCategory("discipline")}
                    color="primary"
                  />
                )}

                {selectedDiscipline.length !== totalDiscipline.length &&
                  selectedDiscipline?.sort((a, b) => a.localeCompare(b))?.map((item, index) => (
                    <Chip
                      key={index}
                      className="category-chip"
                      label={item}
                      onDelete={() => handleDeleteCategory("discipline", item)}
                      color="primary"
                    />
                  ))}

                {/* {!selectedDiscipline.length && ( // commenting this out as for discipline we are showing all the options even when no option is selected
                  <Typography className="no-select">
                    Select at least one option
                  </Typography>
                )} */}
              </AccordionDetails>
            </Accordion>
            <Accordion defaultExpanded className="quiz-selections">
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="panel-organ-system-content"
                id="panel-organ-system-header"
              >
                <Typography className={`header ${classes.heading}`}>Organ System</Typography>
                <Button
                  disableElevation
                  className={classes.clearSelection}
                  color="primary"
                  onClick={(evt) => clearSelection(evt, "organSystem")}
                >
                  {selectedOrgSystem.length > 0 && <>Clear All</>}
                  {!selectedOrgSystem.length && <>Add All</>}
                </Button>
              </AccordionSummary>
              <AccordionDetails id="panel-organ-system-content" className="accordian-summary">
                {selectedOrgSystem.length === totalOrgSystem.length && (
                  <Chip
                    className="category-chip"
                    label={ALL_TEXT.ORGAN_SYSTEM}
                    onDelete={() => handleDeleteAllCategory("organSystem")}
                    color="primary"
                  />
                )}

                {selectedOrgSystem.length !== totalOrgSystem.length &&
                  selectedOrgSystem?.sort((a, b) => a.localeCompare(b))?.map((item, index) => (
                    <Chip
                      key={index}
                      className="category-chip"
                      label={item}
                      onDelete={() => handleDeleteCategory("organSystem", item)}
                      color="primary"
                    />
                  ))}

                {builderTouched && !selectedOrgSystem.length && (
                  <Typography className="no-select">
                    Select at least one option
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
            {examTypeOPtionsSelection()}
          </>
        ) : (
          <>
            <Accordion defaultExpanded className="quiz-selections">
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="panel-topics-content"
                id="panel-topics-header"
              >
                <Typography className={`header ${classes.heading}`}>Topics</Typography>
                <Button
                  disableElevation
                  className={classes.clearSelection}
                  color="primary"
                  onClick={(evt) => clearSelection(evt, "topics")}
                >
                  {selectedTopics.length > 0 && <>Clear All</>}
                  {!selectedTopics.length && searchResultsData?.topics?.length > 0 && <>Add All</>}
                </Button>
              </AccordionSummary>
              <AccordionDetails id="panel-topics-content" className="accordian-summary">
                {selectedTopics?.sort((a, b) => a.localeCompare(b))?.map((item, index) => (
                  <Chip
                    key={index}
                    className="category-chip"
                    label={item}
                    onDelete={() => handleDeleteCategory("topics", item)}
                    color="primary"
                  />
                ))}

                {!selectedTopics.length && (
                  <Typography className="no-select">
                    {searchResultsData?.topics?.length > 0
                      ? "No topics selected"
                      : "Use Power Search to find and select topics"}
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
            <Accordion defaultExpanded className="quiz-selections">
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="panel-subtopics-content"
                id="panel-subtopics-header"
              >
                <Typography className={`header ${classes.heading}`}>SubTopics</Typography>
                <Button
                  disableElevation
                  className={classes.clearSelection}
                  color="primary"
                  onClick={(evt) => clearSelection(evt, "subtopics")}
                >
                  {selectedSubTopics.length > 0 && <>Clear All</>}
                  {!selectedSubTopics.length && searchResultsData?.subtopics?.length > 0 && <>Add All</>}
                </Button>
              </AccordionSummary>
              <AccordionDetails id="panel-subtopics-content" className="accordian-summary">
                {selectedSubTopics?.sort((a, b) => a.localeCompare(b))?.map((item, index) => (
                  <Chip
                    key={index}
                    className="category-chip"
                    label={item}
                    onDelete={() => handleDeleteCategory("subtopics", item)}
                    color="primary"
                  />
                ))}

                {!selectedSubTopics.length && (
                  <Typography className="no-select">
                    {searchResultsData?.subtopics?.length > 0
                      ? "No subtopics selected"
                      : "Use Power Search to find and select subtopics"}
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
            {examTypeOPtionsSelection()}
          </>
        )}
        <Accordion defaultExpanded className="quiz-selections">
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls="panel-question-type-content"
            id="panel-question-type-header"
          >
            <Typography className={`header ${classes.heading}`}>Question Type</Typography>
          </AccordionSummary>
          <AccordionDetails id="panel-question-type-content" className="accordian-summary">
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(evt) => selectQuestionType(evt, "all", typeCount)}
                    checked={questionOptions.all}
                    name="all"
                    color="primary"
                  />
                }
                label={`All`}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(evt) => selectQuestionType(evt, "unused", typeCount)}
                    checked={questionOptions.unused}
                    name="unused"
                    color="primary"
                  />
                }
                label={`Unused`}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(evt) => selectQuestionType(evt, "correct", typeCount)}
                    checked={questionOptions.correct}
                    name="correct"
                    color="primary"
                  />
                }
                label={`Correct`}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(evt) => selectQuestionType(evt, "incorrect", typeCount)}
                    checked={questionOptions.incorrect}
                    name="incorrect"
                    color="primary"
                  />
                }
                label={`Incorrect`}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(evt) => selectQuestionType(evt, "marked", typeCount)}
                    checked={questionOptions.marked}
                    disabled={!questionOptions?.incorrect && !questionOptions?.correct}
                    name="marked"
                    color="primary"
                  />
                }
                label={`Marked`}
              />
              {/* Audio-mode "Has audio" facet — flag+audience gated (dark by
                  default), a FILTER layered on the type selection above, not a
                  question type itself. Server-side enforcement is authoritative. */}
              {audioModeAvailable() && (
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={(evt) => setHasAudioFacet(evt.target.checked)}
                      checked={hasAudioFacet}
                      name="hasAudio"
                      color="primary"
                      inputProps={{ "data-testid": "has-audio-facet" }}
                    />
                  }
                  label={`Has audio`}
                />
              )}
            </FormGroup>

            {builderTouched && !selectedQuesTypeLength && (
              <Typography className="no-select">
                Select at least one option
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
        <Accordion defaultExpanded className="quiz-selections">
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls="panel-quiz-options-content"
            id="panel-quiz-options-header"
          >
            <Typography className={`header ${classes.heading}`}>Quiz Options</Typography>
          </AccordionSummary>
          <AccordionDetails id="panel-quiz-options-content">
            <Grid container alignItems="center">
              <Grid item xs={12} md={4} sm={4}>
                <Grid container alignItems="center">
                  <Grid item>Timer</Grid>
                  <Grid item>
                    <Switch
                      checked={timerMode}
                      onChange={(e) => toggleTimerMode(e)}
                      name="timer"
                      color="primary"
                    />
                  </Grid>
                  <Grid item>
                    {timerMode && <>On</>}
                    {!timerMode && <>Off</>}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={4} sm={4}>
                <Grid container alignItems="center">
                  <Grid item>Save settings</Grid>
                  <Grid item>
                    <Switch
                      checked={saveSettings}
                      onChange={(e) => toggleSaveSettings(e)}
                      name="saveSettings"
                      color="primary"
                    />
                  </Grid>
                  <Grid item>
                    {saveSettings && <>On</>}
                    {!saveSettings && <>Off</>}
                  </Grid>
                </Grid>
              </Grid>
              {CONSTANTS.thresholdFeatureEnabled && selectedExamType.some(exam => THRESHOLD_ENABLED_EXAM_TYPES.includes(exam)) && (
                <Grid item xs={12} md={4} sm={4}>
                  <Grid container alignItems="center">
                    <Grid item className="high-yield-label">
                      High-Yield only
                      <Tooltip
                        arrow
                        placement="top"
                        enterTouchDelay={0}
                        leaveTouchDelay={4000}
                        classes={{ tooltip: classes.customTooltip }}
                        title="Shows only the questions flagged as highest-yield for the ABSITE. Turn off to include every available question."
                      >
                        {/* A7: 44px touch target — the bare 16px icon was well
                            below the minimum on a flow that fully stacks on
                            phones. */}
                        <IconButton
                          size="small"
                          className="info-tooltip-btn"
                          aria-label="About High-Yield only"
                          data-testid="high-yield-info"
                        >
                          <InfoOutlined className="info-tooltip" />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                    <Grid item>
                      <Switch
                        checked={thresholdMode}
                        onChange={(e) => toggleThresholdMode(e)}
                        name="threshold"
                        color="primary"
                        inputProps={{ 'aria-label': 'High-Yield only' }}
                      />
                    </Grid>
                    <Grid item>
                      {thresholdMode && <>On</>}
                      {!thresholdMode && <>Off</>}
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {isTutor &&
                <Grid item xs={12} md={4} sm={4}>
                  <Grid container alignItems="center">
                    <Grid item>Tutor Mode</Grid>
                    <Grid item>
                      <Switch
                        checked={tutorMode}
                        onChange={(e) => toggleTutorMode(e)}
                        name="timer"
                        color="primary"
                      />
                    </Grid>
                    <Grid item>
                      {tutorMode && <>On</>}
                      {!tutorMode && <>Off</>}
                    </Grid>
                  </Grid>
                </Grid>
              }
            </Grid>
          </AccordionDetails>
        </Accordion>
      </>
    );
  };

  const handleSubDateClose = () => {
    setSubDateOpen(false);
  };

  const handleQuizErrClose = () => {
    setQuizErr(false);
    setQuizErrMsg('');
  };

  const handleQuizSuccessClose = () => {
    dispatch(resetSubmitQuiz());
    setQuizSuccessOpen(false);
  };

  const onTutorModeConfirm = (isConfirm) => {
    setIsTutorConfirmModal(false);

    if (isConfirm) {
      onCreateQuiz(true);
    }
  };

  const gotToSubscription = () => {
    return history.push('subscriptions');
  }

  const onAgreementSubmit = () => {
    setOpenAgreementModal(false);
    if (userId) {
      dispatch(getPreference(userId));
      dispatch(getCategories());

      setTimeout(() => {
        dispatch(getCategoryPreferences(userId));
      }, 1000);
    }
  }

  useEffect(() => {
    if (submitQuizRes?.quizStatus === "COMPLETE" && quizSubmittedRef.current) {
      setQuizSuccessOpen(true);
      quizSubmittedRef.current = false;
      dispatch(getOverallQuizHistory(userId));

      dispatch(getOverallStudentQuizHistory({ userId: userId, isCompleted: false }));

      dispatch(resetSubmitQuiz());
    }
  }, [submitQuizRes]);

  // Main filter change effect to get updated question counts
  useEffect(() => {
    dispatch(resetQuiz());
    const savedUserId = window.localStorage.getItem("userId");

    // Trigger question count retrieval as long as the user is identified and
    // at least one question type is selected. We no longer require category
    // selections when Power Search is off so that toggling question types like
    // 'Unused' immediately refreshes the available-question count.

    // For Power Search mode, require topics OR subtopics to be selected
    const isPowerSearchValid = !toggleSearchSwitch || (selectedTopics.length > 0 || selectedSubTopics.length > 0);

    if ((user || userDetailsFromToken || savedUserId) && selectedQuesTypeLength && isPowerSearchValid) {
      debouncedGetQuestionCount();
    } else if (toggleSearchSwitch && !selectedTopics.length && !selectedSubTopics.length) {
      // Power Search with no topics/subtopics - reset to 0
      setQuesCountData({});
      dispatch(resetQuesCount());
    }
  }, [user, userDetailsFromToken, selectedDiscipline, selectedOrgSystem, selectedExamType,
    selectedTopics, selectedSubTopics, questionOptions, thresholdMode, dispatch, toggleSearchSwitch, selectedQuesTypeLength,
    debouncedGetQuestionCount]);

  // Explain WHY the Create Quiz button is disabled, so a stuck user gets a
  // concrete next step instead of a dead grey button. Mirrors the exact
  // disableCreateQuizBtn conditions (see the setIsCreateQuizDisabled effect
  // above), in the same priority order: category selection first, then the
  // question-count field. Empty string == nothing blocking (button enabled).
  const createQuizBlockReason = (() => {
    // A quiz in progress makes the whole panel inert (the real blocker is the
    // in-progress quiz, surfaced by the Resume/End UI) — don't advise here.
    if (quizProgressId) return "";
    if (!toggleSearchSwitch) {
      const missing = [];
      if (!selectedDiscipline.length) missing.push("a discipline");
      if (!selectedOrgSystem.length) missing.push("an organ system");
      if (!selectedExamType.length) missing.push("an exam type");
      if (missing.length) return `Select ${joinWithAnd(missing)} to build your quiz.`;
    } else {
      const missing = [];
      if (!selectedExamType.length) missing.push("an exam type");
      if (!selectedTopics.length && !selectedSubTopics.length) missing.push("at least one topic or subtopic");
      if (missing.length) return `Select ${joinWithAnd(missing)} to build your quiz.`;
    }
    // Categories are set but nothing matches — entering a number can't help.
    if (quesCountData?.eligibleQuestionCount === 0) {
      return "No questions match your current selections — try different filters or question types.";
    }
    if (!questionLength) return "Enter how many questions you'd like.";
    if (questionLength > quesCountData?.eligibleQuestionCount) {
      const n = quesCountData?.eligibleQuestionCount || 0;
      return `Only ${n} question${n === 1 ? "" : "s"} available right now — lower the number.`;
    }
    // Name is auto-filled, so it's usually the last thing standing when a user
    // has cleared it — enforce the "Required" label up front instead of letting
    // the submit round-trip fail into a transient "Quiz Name is required" toast.
    if (!quizName.trim()) return "Enter a name for your quiz.";
    return "";
  })();

  return (
    <>
      <Backdrop className={classes.backdrop} open={isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {openAgreementModal &&
        <UserAgreement onAgreementSubmit={onAgreementSubmit}></UserAgreement>
      }

      <Snackbar open={subDateOpen} onClose={handleSubDateClose} autoHideDuration={10000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Box className="welcome-alert">
          Medlearnity is excited to launch QLearn in Beta. Please share feedback if you notice any issues or have ideas for improvements! Thank you
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleSubDateClose}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Snackbar>

      <Snackbar open={quizErr} onClose={handleQuizErrClose} autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Box className="sub-alert">
          {quizErrMsg}
        </Box>
      </Snackbar>

      <Snackbar open={quizSuccessOpen} onClose={handleQuizSuccessClose} autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Box className="success-alert">
          Quiz ended successfully!
        </Box>
      </Snackbar>

      <Modal
        className='tutor-mode-confirm-modal'
        open={isTutorConfirmModal}
        onClose={() => onTutorModeConfirm(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Fade in={isTutorConfirmModal}>
          <Box className='modal-container'>
            <Typography variant="h5" className="header">Warning!</Typography>
            <Typography variant="body2" >
              You're entering the next window with <b>'Tutor Mode'</b> and <b>'Timer'</b> activated.
              These settings may affect your workflow. <br /> <br /> To proceed,
              click 'Continue'. To change settings, click 'Change Settings'.
            </Typography>

            <Box className="apply-btn">
              <Button variant="contained" onClick={() => onTutorModeConfirm(false)}>
                Change Settings
              </Button>
              <Button variant="contained" className="end-sub-btn" color="primary" onClick={() => onTutorModeConfirm(true)}>
                Continue
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      <Modal
        className='tutor-mode-confirm-modal sub-end-modal'
        open={isQuestionLimitModalOpen}
        onClose={() => setIsQuestionLimitModalOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Fade in={isQuestionLimitModalOpen}>
          <Box className='modal-container'>
            <Typography variant="h5" className="header">Attention</Typography>
            <Typography variant="body2" >
              You've run out of questions for your plan today! <br /> Please upgrade your plan if applicable or come back tomorrow.
            </Typography>

            <Box className="apply-btn">
              <Button variant="contained" onClick={() => setIsQuestionLimitModalOpen(false)}>
                Close
              </Button>
              <Button variant="contained" className="end-sub-btn" color="primary" onClick={gotToSubscription}>
                Subscriptions
              </Button>
              <Button variant="contained" color="secondary" onClick={() => { setIsQuestionLimitModalOpen(false); setIsShareModalOpen(true); }}>
                Share & Earn Free Pro
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      <ShareEarnModal open={isShareModalOpen} onClose={() => setIsShareModalOpen(false)} />

      <Box className={classes.container}>
        <Menu />
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          className="quiz-container"
        >
          {/*
            * The banner lives INSIDE the content column (full-width grid item),
            * not as a direct child of classes.container: that parent is a
            * no-wrap 100vh flex ROW (Menu | content), and a banner sibling
            * there gets stretched into a full-height column — a ~200px
            * squeezed strip beside the form on phones, and a hollow column
            * with the CTA orphaned at the bottom on desktop (F3 create-quiz
            * UX audit 2026-07-06, P1 items 1/4/6). As a grid item it flows
            * above the form in the horizontal-strip form factor its scss was
            * written for.
            */}
          {showQuickstart && !quickstartDismissed && (
            <Grid item xs={12} className="quickstart-banner-item">
              <QuickstartBanner
                onStart={onQuickstart}
                onCustomize={onQuickstartCustomize}
                onExamPick={onQuickstartExamPick}
                loading={!!createQuizReq?.loading}
                examTypes={examType}
                highYieldExamTypes={quickstartHighYieldExamTypes}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={3} className='category-column'>
            <Paper
              elevation={0}
              className={`app-body-left-container ${toggleSearchSwitch ? 'power-search-enabled' : 'power-search-disabled'}`}
            >
              <Categories
                onDisciplineCatSelect={onDisciplineCatSelect}
                onOrgSysCatSelect={onOrgSysCatSelect}
                onExamTypeCatSelect={onExamTypeCatSelect}
                onTopicsCatSelect={onTopicsCatSelect}
                onSubTopicsCatSelect={onSubTopicsCatSelect}
                selectedDiscipline={selectedDiscipline}
                selectedOrgSystem={selectedOrgSystem}
                selectedExamType={selectedExamType}
                selectedTopics={selectedTopics}
                selectedSubTopics={selectedSubTopics}
                totalDiscipline={totalDiscipline}
                totalOrgSystem={totalOrgSystem}
                examType={examType}
                isViewDisabled={!!quizProgressId}
                onPowerSearchModeChange={setToggleSearchSwitch}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Paper elevation={0} className="app-body-right-container">
              <Box className="user-container">
                <Box className="user-welcome">
                  <Box>
                    {/* Only greet by name once it has loaded — otherwise
                        studentDetails is async and this rendered the stray
                        "Hi , Welcome to QLearn!" (floating comma + gap) on
                        every load until it resolved. */}
                    {userName ? `Hi ${userName}, ` : ""}Welcome to QLearn!
                    {/* Beta status: an inline tag beside the heading. Replaces the
                        old absolute 45°-rotated corner ribbon, which anchored to
                        the container (not the viewport) and overlapped/clipped
                        this welcome line at narrow widths (CQ-4). */}
                    <Box component="span" className="beta-tag">Beta</Box>
                    {userRole &&
                      <Box component="span" className="user-role">{userRole}</Box>
                    }
                  </Box>
                  {plan?.plan &&
                    <Tooltip arrow classes={{ tooltip: classes.customTooltip }}
                      title={`${plan?.plan?.name}\nDaily Questions Remaining: ${quesCountData?.eligibleQuestionCount}`}>
                      <Box component="p" onClick={gotToSubscription} className={`plan-title-ctx crt-qz ${plan?.expired ? 'expired' : ''}`}>
                        Plan: QLearn {plan?.plan?.category}
                      </Box>
                    </Tooltip>
                  }
                </Box>

                {!!quizProgressId && (
                  <Box className="user-resume">
                    <Box className="user-resume-text">
                      {quizProgressQuiz?.assignedByTutorId ? (
                        <>
                          Your tutor assigned you a quiz
                          {hasCustomAssignedName(quizProgressQuiz) &&
                            <>: &ldquo;{quizProgressQuiz.name.trim()}&rdquo;</>
                          }
                          . Start it when you&apos;re ready.
                        </>
                      ) : (
                        <>
                          Looks like you have a quiz in progress. Please, complete
                          your quiz first.
                        </>
                      )}
                    </Box>
                    <Button
                      variant="contained"
                      onClick={onResumeQuiz}
                      className="resume-btn"
                    >
                      {quizProgressQuiz?.assignedByTutorId ? "Start quiz" : "Resume"}
                    </Button>
                    {/*
                      * No destructive affordance on a tutor-assigned quiz that
                      * hasn't been started: this bar's assigned branch is by
                      * construction the pre-start state ("Start it when you're
                      * ready"), and a red End Quiz one tap from Start invited
                      * destroying the assignment (F3 UX audit 2026-07-06, P2
                      * item 8 — the client persona hesitated exactly here).
                      * A student who has begun the quiz can still end it from
                      * the exam surface itself.
                      */}
                    {!quizProgressQuiz?.assignedByTutorId && (
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={onEndQuiz}
                        className="redButton"
                      >
                        End Quiz
                      </Button>
                    )}
                  </Box>
                )}
              </Box>

              {/* A9: the page's sole task heading is now a real <h1> (was <h2>
                  with no h1 above it — a skipped-level outline for screen
                  readers). Visual size is pinned in scss so the promotion is
                  semantic-only. */}
              <Box className={!!quizProgressId ? "is-disabled quiz-header-container" : "quiz-header-container"}>
                <h1 className="quiz-header"> Quiz me on: </h1>
              </Box>

              <Box
                className={!!quizProgressId ? "is-disabled" : ""}
              >
                {CONSTANTS.semanticSearchEnabled && (
                  <PowerSearchPanel
                    examTypes={selectedExamType}
                    onBuildQuiz={onPowerSearchBuildQuiz}
                    buildingQuiz={!!createQuizReq?.loading}
                  />
                )}
                {renderQuizOptionSelections()}

                <p>
                  {CONSTANTS.thresholdFeatureEnabled && thresholdMode && selectedExamType.some(exam => THRESHOLD_ENABLED_EXAM_TYPES.includes(exam)) && (
                    <>
                      <span data-testid='ques-cnt-txt' className={`ques-cnt-txt ${builderTouched && !quesCountData?.highYieldCount ? 'zero' : ''}`}>
                        {quesCountData?.highYieldCount || 0}
                      </span> High-Yield questions +
                    </>
                  )}
                  <span className={`ques-cnt-txt ${builderTouched && !quesCountData?.questionCount ? 'zero' : ''}`}>
                    {quesCountData?.questionCount || 0}
                  </span> <b>Total Questions Available</b>
                </p>

                <Grid className="lower-container" container>
                  <Grid sm={6} item md={5}>
                    <TextField
                      className={classes.textField}
                      id="quiz-name"
                      inputRef={quizNameInputRef}
                      value={quizName}
                      onChange={changeQuizName}
                      label="Quiz Name (Required)"
                    />
                  </Grid>
                  <Grid sm={4} item md={5}>
                    <label className="ques-lbl">
                      # of Questions
                      (<span className={`ques-cnt-txt ${builderTouched && !quesCountData?.questionCount ? 'zero' : ''}`}>
                        {max || 0} Max</span>)
                      {/* A4: tooltip-ONLY (i). It used to navigate to
                          /subscriptions onClick — on touch, where there is no
                          hover, tapping the (i) yanked the user off the builder
                          mid-setup. enterTouchDelay={0} shows the hint on tap; a
                          44px IconButton (A7) gives it a real touch target; the
                          upsell action moves to the explicit "Upgrade" link. */}
                      <Tooltip arrow placement="top" enterTouchDelay={0} leaveTouchDelay={4000}
                        classes={{ tooltip: classes.customTooltip }}
                        title={`Upgrade your plan for more daily questions.`}>
                        <IconButton
                          size="small"
                          className="info-tooltip-btn"
                          aria-label="About the daily question limit"
                          data-testid="ques-count-info"
                        >
                          <InfoOutlined className="info-tooltip" />
                        </IconButton>
                      </Tooltip>
                      <Button
                        className={`upgrade-link ${classes.upgradeLink}`}
                        color="primary"
                        onClick={() => {
                          // §6 gap-fill: this ques-count paywall entry point
                          // previously fired zero analytics. Track then navigate.
                          trackPaywallEntry('paywall_entry_clicked', { source: 'ques_count_link' });
                          gotToSubscription();
                        }}
                        data-testid="ques-count-upgrade"
                      >
                        Upgrade
                      </Button>
                    </label>
                    <TextField
                      className={`${classes.textField} ${(!questionLength || questionLength > quesCountData?.eligibleQuestionCount) ? 'empty-text' : ''}`}
                      value={questionLength}
                      // type="number"
                      onChange={changeQuestionsLength}
                      // A10: digits-only field — surface the numeric keypad on
                      // mobile instead of the default QWERTY (the regex guard in
                      // changeQuestionsLength still enforces digits).
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*", "aria-label": "Number of questions" }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">
                            / {quesCountData?.eligibleQuestionCount || 0}
                          </InputAdornment>
                        ),
                      }}
                      helperText={`${(questionLength > quesCountData?.eligibleQuestionCount) ? 'Not enough questions available' : (!questionLength && quesCountData?.eligibleQuestionCount !== 0) ? 'Amount of questions cannot be empty' : ''}`}
                    />
                  </Grid>

                  <Grid item sm={12} md={2} className="create-quiz-grid">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => onCreateQuiz(false)}
                      // disabled={disableCreateQuizBtn}
                      disabled={isCreateQuizDisabled}
                    >
                      Create Quiz
                    </Button>
                  </Grid>
                  {driveModeAvailable() && (
                    <Grid item sm={12} md={2} className="create-quiz-grid">
                      <Button
                        type="button"
                        color="primary"
                        variant="outlined"
                        disabled={isCreateQuizDisabled}
                        onClick={onDriveMode}
                        data-testid="start-drive-mode"
                      >
                        Audio Mode
                      </Button>
                    </Grid>
                  )}
                </Grid>

                {/* Persistent live region (always in the DOM) so the hint is
                    reliably announced when it appears — inserting a fresh
                    role=status node is inconsistently spoken across SR/browsers. */}
                <div className="create-quiz-hint-live" role="status" aria-live="polite">
                  {createQuizBlockReason && (
                    <Typography className="create-quiz-hint" data-testid="create-quiz-hint">
                      {createQuizBlockReason}
                    </Typography>
                  )}
                </div>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/*
        * A2: sticky/fixed Create-Quiz action bar for phones (≤600px only; CSS
        * hides it above that). On mobile the primary CTA sits at the bottom of a
        * 5-accordion stack with no persistent anchor, so first-quiz activation
        * demanded a long blind scroll. This bar keeps the count + CTA reachable
        * while the filters are scrolled.
        *
        * Fixed (not position:sticky): the CTA's scroll ancestor
        * (.app-body-right-container) is overflow:hidden, and iOS Safari drops
        * sticky inside overflow containers — a viewport-fixed bar is the robust
        * choice. It is a top-level sibling so no transformed/overflow ancestor
        * can clip it; scroll content gets padding-bottom (scss) so the bar never
        * covers the last accordion. Hidden while a quiz is in progress (the form
        * is inert then and the Resume/End bar is the real action).
        */}
      {!quizProgressId && (
        <Box className="mobile-create-bar" data-testid="mobile-create-bar">
          <Box className="mobile-create-bar-info">
            {createQuizBlockReason ? (
              <Typography className="mobile-create-bar-hint" data-testid="mobile-create-bar-hint">
                {createQuizBlockReason}
              </Typography>
            ) : (
              <Typography className="mobile-create-bar-count">
                <span className="ques-cnt-txt">{quesCountData?.eligibleQuestionCount || 0}</span>
                {" "}questions ready
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            color="primary"
            className="mobile-create-btn"
            onClick={() => onCreateQuiz(false)}
            disabled={isCreateQuizDisabled}
            data-testid="mobile-create-quiz"
          >
            Create Quiz
          </Button>
        </Box>
      )}
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    height: "100vh",
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
  heading: {
    fontSize: theme.typography.h4,
    fontWeight: theme.typography.fontWeightMedium,
    flexBasis: "92%",
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  clearSelection: {
    fontSize: theme.typography.pxToRem(12),
    border: "none",
    // AA fix (parity sweep): MUI primary (#3f51b5) read 2.2:1 on the dark
    // #262626 surface. Route to the theme-aware --list-item-sel-color
    // (light #3f51b5 unchanged, dark #7a8ae7 ~4.77:1). Compound selector beats
    // MUI's .MuiButton-textPrimary specificity without !important.
    "&.MuiButton-textPrimary": { color: "var(--list-item-sel-color)" },
    // A7: 44px touch target — was padding:0, well below the minimum on a flow
    // that fully stacks on phones. Negative margin keeps the accordion header
    // height visually unchanged while the hit area grows.
    minHeight: 44,
    minWidth: 44,
    padding: "8px 10px",
    marginTop: -8,
    marginBottom: -8,
  },
  upgradeLink: {
    // A4: the explicit upsell affordance the (i) icon used to (mis)carry.
    fontSize: theme.typography.pxToRem(12),
    minHeight: 44,
    padding: "8px 6px",
    marginLeft: 2,
    textTransform: "none",
    // AA fix (parity sweep): same MUI-primary-on-dark 2.2:1 issue as the
    // clear/add buttons — route to the theme-aware token.
    "&.MuiButton-textPrimary": { color: "var(--list-item-sel-color)" },
  },
  textField: {
    width: "92%",
  },
  customTooltip: {
    whiteSpace: 'pre-wrap'
  }
}));
